/**
 * webvtt-parser.js — Minimaler WebVTT-Parser fuer den Browser-Sub-Pfad (F1 W4).
 *
 * zapd liefert fertiges WebVTT pro Sidecar-Segment (`subs-N-NNNNN.vtt`), kein
 * OCR/Decode noetig — wir brauchen nur Text + Cue-Timing. Bewusst KEIN
 * vollstaendiger WebVTT-Renderer (kein Karaoke/Positioning/Region): die Cues
 * landen als plain text in einem DOM-Overlay (textContent, XSS-safe), so wie
 * der Shell-Pfad in shell/cue-overlay.js.
 *
 * Unterstuetzt:
 *   - `HH:MM:SS.mmm` und `MM:SS.mmm` Timestamps
 *   - mehrzeilige Cue-Texte
 *   - optionale Cue-ID-Zeile vor dem Timing
 *   - `X-TIMESTAMP-MAP=MPEGTS:<n>,LOCAL:<ts>` (HLS-WebVTT-Offset)
 *   - Tag-Strip (`<c>`, `<i>`, `<v Bob>` …) → plain text
 */

/** `HH:MM:SS.mmm` | `MM:SS.mmm` → Sekunden (float), oder null. */
function parseTimestamp(s) {
  const m = /^(?:(\d+):)?(\d{1,2}):(\d{2})\.(\d{1,3})$/.exec(s.trim());
  if (!m) return null;
  const h   = m[1] ? parseInt(m[1], 10) : 0;
  const min = parseInt(m[2], 10);
  const sec = parseInt(m[3], 10);
  const ms  = parseInt(m[4].padEnd(3, '0'), 10);
  return h * 3600 + min * 60 + sec + ms / 1000;
}

/** X-TIMESTAMP-MAP → Offset-Sekunden, der auf lokale Cue-Zeiten addiert wird.
 *  offset = MPEGTS/90000 − LOCAL. Fehlt der Header oder ist er 0/0 → 0. */
function parseTimestampMap(line) {
  const mpegtsM = /MPEGTS:(\d+)/.exec(line);
  const localM  = /LOCAL:([\d:.]+)/.exec(line);
  const mpegts  = mpegtsM ? parseInt(mpegtsM[1], 10) / 90000 : 0;
  const local   = localM ? (parseTimestamp(localM[1]) ?? 0) : 0;
  return mpegts - local;
}

/** Entfernt WebVTT-Inline-Tags + Cue-Voice-Spans → plain text. */
function stripTags(text) {
  return text.replace(/<[^>]+>/g, '');
}

/**
 * Parse ein WebVTT-Dokument → Array von Cues.
 * @param {string} text  Roh-WebVTT (ein Sidecar-Segment).
 * @returns {{startSec:number,endSec:number,text:string}[]} chronologisch.
 */
export function parseWebVtt(text) {
  if (typeof text !== 'string' || text.indexOf('WEBVTT') === -1) return [];
  // Normalisiere Zeilenenden, splitte in Bloecke an Leerzeilen.
  const lines  = text.replace(/\r\n?/g, '\n').split('\n');
  let offsetSec = 0;
  const cues = [];

  // Header-Region: bis zur ersten Leerzeile. X-TIMESTAMP-MAP rausziehen.
  let i = 0;
  for (; i < lines.length; i++) {
    const l = lines[i];
    if (l.trim() === '') { i++; break; }
    if (l.startsWith('X-TIMESTAMP-MAP')) offsetSec = parseTimestampMap(l);
  }

  // Cue-Bloecke.
  let block = [];
  const flush = () => {
    if (block.length === 0) return;
    // Timing-Zeile finden (enthaelt '-->').
    let tIdx = block.findIndex((l) => l.includes('-->'));
    if (tIdx === -1) { block = []; return; }      // NOTE/STYLE/Region-Block → skip
    const timing = block[tIdx];
    const arrowM = /([\d:.]+)\s*-->\s*([\d:.]+)/.exec(timing);
    if (!arrowM) { block = []; return; }
    const start = parseTimestamp(arrowM[1]);
    const end   = parseTimestamp(arrowM[2]);
    if (start === null || end === null) { block = []; return; }
    const body = block.slice(tIdx + 1).join('\n').trim();
    const clean = stripTags(body).trim();
    if (clean !== '') {
      cues.push({ startSec: start + offsetSec, endSec: end + offsetSec, text: clean });
    }
    block = [];
  };

  for (; i < lines.length; i++) {
    if (lines[i].trim() === '') flush();
    else block.push(lines[i]);
  }
  flush();
  return cues;
}
